import React, { InputHTMLAttributes, TextareaHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  isRequired?: boolean;
}

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  isRequired?: boolean;
}

export interface SelectProps extends InputHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: Array<{ value: string; label: string }>;
  isRequired?: boolean;
}

/**
 * Input component following Notion design patterns
 * - Height: 40px
 * - Padding: 12px horizontal
 * - Border: 1px solid #D0D0D0
 * - Border radius: 8px
 * - Font: 14px
 */
export const Input: React.FC<InputProps> = ({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  isRequired = false,
  className = '',
  type = 'text',
  children,
  ...props
}) => {
  const inputBaseClasses =
    'w-full h-10 px-3 text-base bg-white border rounded-md ' +
    'text-black placeholder-black-lightest ' +
    'transition-all duration-200 ' +
    'focus:outline-none focus:border-orange-400 focus:shadow-focus ' +
    'disabled:bg-cream-100 disabled:text-black-lighter disabled:cursor-not-allowed';

  const inputStateClasses = error
    ? 'border-error-500 focus:border-error-500 focus:shadow-focus-error'
    : 'border-border hover:border-border-strong';

  const combinedInputClasses = `
    ${inputBaseClasses}
    ${inputStateClasses}
    ${leftIcon ? 'pl-10' : ''}
    ${rightIcon ? 'pr-10' : ''}
    ${type === 'select' ? 'pr-10 appearance-none cursor-pointer' : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  // Handle select element
  if (type === 'select') {
    return (
      <div className="w-full">
        {label && (
          <label className="block mb-2 text-base font-semibold text-black">
            {label}
            {isRequired && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select className={combinedInputClasses} {...props as any}>
            {children}
          </select>

          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-black-lighter">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-1 mt-1.5 text-xs text-error-500">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {!error && helperText && (
          <div className="mt-1.5 text-xs text-black-lighter">
            {helperText}
          </div>
        )}
      </div>
    );
  }

  // Handle regular input
  return (
    <div className="w-full">
      {label && (
        <label className="block mb-2 text-base font-semibold text-black">
          {label}
          {isRequired && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-black-lighter">
            <div className="w-4 h-4">{leftIcon}</div>
          </div>
        )}

        <input
          type={type}
          className={combinedInputClasses}
          {...props}
        />

        {rightIcon && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black-lighter">
            <div className="w-4 h-4">{rightIcon}</div>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1.5 text-xs text-error-500">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {!error && helperText && (
        <div className="mt-1.5 text-xs text-black-lighter">
          {helperText}
        </div>
      )}
    </div>
  );
};

export const Textarea: React.FC<TextareaProps> = ({
  label,
  error,
  helperText,
  isRequired = false,
  className = '',
  rows = 4,
  ...props
}) => {
  const textareaBaseClasses =
    'w-full px-3 py-3 text-base bg-white border rounded-md ' +
    'text-black placeholder-black-lightest ' +
    'transition-all duration-200 ' +
    'focus:outline-none focus:border-orange-400 focus:shadow-focus ' +
    'disabled:bg-cream-100 disabled:text-black-lighter disabled:cursor-not-allowed ' +
    'resize-y';

  const textareaStateClasses = error
    ? 'border-error-500 focus:border-error-500 focus:shadow-focus-error'
    : 'border-border hover:border-border-strong';

  const combinedClasses = `
    ${textareaBaseClasses}
    ${textareaStateClasses}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full">
      {label && (
        <label className="block mb-2 text-base font-semibold text-black">
          {label}
          {isRequired && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <textarea
        className={combinedClasses}
        rows={rows}
        {...props}
      />

      {error && (
        <div className="flex items-center gap-1 mt-1.5 text-xs text-error-500">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {!error && helperText && (
        <div className="mt-1.5 text-xs text-black-lighter">
          {helperText}
        </div>
      )}
    </div>
  );
};

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  helperText,
  options,
  isRequired = false,
  className = '',
  ...props
}) => {
  const selectBaseClasses =
    'w-full h-10 px-3 pr-10 text-base bg-white border rounded-md ' +
    'text-black ' +
    'transition-all duration-200 ' +
    'focus:outline-none focus:border-orange-400 focus:shadow-focus ' +
    'disabled:bg-cream-100 disabled:text-black-lighter disabled:cursor-not-allowed ' +
    'appearance-none cursor-pointer';

  const selectStateClasses = error
    ? 'border-error-500 focus:border-error-500 focus:shadow-focus-error'
    : 'border-border hover:border-border-strong';

  const combinedClasses = `
    ${selectBaseClasses}
    ${selectStateClasses}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className="w-full">
      {label && (
        <label className="block mb-2 text-base font-semibold text-black">
          {label}
          {isRequired && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <select className={combinedClasses} {...props}>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-black-lighter">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-1.5 text-xs text-error-500">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {!error && helperText && (
        <div className="mt-1.5 text-xs text-black-lighter">
          {helperText}
        </div>
      )}
    </div>
  );
};

/**
 * Checkbox component
 */
export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  className = '',
  ...props
}) => {
  if (label) {
    return (
      <label className="flex items-center gap-2 cursor-pointer group">
        <input
          type="checkbox"
          className={`w-4 h-4 text-orange-400 border-border rounded focus:ring-2 focus:ring-orange-400 focus:ring-offset-0 cursor-pointer ${className}`}
          {...props}
        />
        <span className="text-base text-black group-hover:text-black-light">
          {label}
        </span>
      </label>
    );
  }

  return (
    <input
      type="checkbox"
      className={`w-4 h-4 text-orange-400 border-border rounded focus:ring-2 focus:ring-orange-400 focus:ring-offset-0 cursor-pointer ${className}`}
      {...props}
    />
  );
};

/**
 * Radio component
 */
export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
}

export const Radio: React.FC<RadioProps> = ({
  label,
  className = '',
  ...props
}) => {
  return (
    <label className="flex items-center gap-2 cursor-pointer group">
      <input
        type="radio"
        className={`w-4 h-4 text-orange-400 border-border focus:ring-2 focus:ring-orange-400 focus:ring-offset-0 cursor-pointer ${className}`}
        {...props}
      />
      <span className="text-base text-black group-hover:text-black-light">
        {label}
      </span>
    </label>
  );
};
